import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { portfolioImages, portfolioCategories } from '../data/mock';

// Lazy loading image component
const LazyImage = ({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`${className} cursor-pointer`} onClick={onClick}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
};

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('blackwhite');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const filteredImages = portfolioImages.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <section id="portfolio" className="py-24 md:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-poppins text-xs tracking-[0.4em] text-gray-400 mb-4 block">
            WORK
          </span>
          <h2 className="font-bebas text-5xl md:text-6xl tracking-wider">
            Portfolio
          </h2>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12">
          {portfolioCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`font-poppins text-sm tracking-wider transition-all duration-300 pb-2 border-b-2 ${
                activeCategory === category.id
                  ? 'text-black border-black'
                  : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image, index) => (
            <div
              key={image.id}
              className="group relative aspect-[3/4] overflow-hidden cursor-pointer"
            >
              <LazyImage
                src={image.url}
                alt={image.title}
                className="absolute inset-0"
                onClick={() => openLightbox(index)}
              />
              {/* Hover Overlay */}
              <div 
                onClick={() => openLightbox(index)}
                className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-end pointer-events-none group-hover:pointer-events-auto"
              >
                <div className="p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="font-bebas text-white text-lg tracking-wider">
                    {image.title}
                  </h3>
                  <p className="font-poppins text-white/70 text-xs tracking-wider">
                    {image.photographer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox/Slideshow */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50 p-2"
            aria-label="Close lightbox"
          >
            <X size={32} strokeWidth={1} />
          </button>

          {/* Navigation */}
          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 p-2"
            aria-label="Previous image"
          >
            <ChevronLeft size={48} strokeWidth={1} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 p-2"
            aria-label="Next image"
          >
            <ChevronRight size={48} strokeWidth={1} />
          </button>

          {/* Main Image */}
          <div className="max-w-5xl max-h-[85vh] px-16">
            <img
              src={filteredImages[currentImageIndex]?.url}
              alt={filteredImages[currentImageIndex]?.title}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          {/* Image Info */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
            <h3 className="font-bebas text-white text-2xl tracking-wider">
              {filteredImages[currentImageIndex]?.title}
            </h3>
            <p className="font-poppins text-white/60 text-sm tracking-wider">
              {filteredImages[currentImageIndex]?.photographer}
            </p>
            <p className="font-poppins text-white/40 text-xs mt-2">
              {currentImageIndex + 1} / {filteredImages.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
